import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { DocumentArrowDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

const REPORTS = [
  {
    id: "monthly-production",
    title: "Producción mensual",
    description: "Fianzas por mes con comisiones estimadas y resumen anual.",
  },
  {
    id: "comision-afianzadora",
    title: "Comisión por afianzadora",
    description: "Resumen de comisiones estimadas por afianzadora.",
  },
  {
    id: "produccion-responsable",
    title: "Producción por responsable",
    description: "Producción y comisiones por miembro del equipo.",
  },
  {
    id: "produccion-cliente",
    title: "Producción por cliente",
    description: "Producción y comisiones por cliente.",
  },
  {
    id: "tarifas-cliente",
    title: "Tarifas por cliente",
    description: "Análisis de tarifas promedio por cliente.",
  },
];

export function ExportPDFDialog({
  isOpen,
  onClose,
  year,
  currentReportId,
  onExportReports,
}) {
  const [selectedReports, setSelectedReports] = useState(new Set([currentReportId]));
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({ current: 0, total: 0, currentName: '' });
  const [error, setError] = useState(null);

  // Reset selection when dialog opens
  useEffect(() => {
    if (isOpen) {
      setSelectedReports(new Set([currentReportId]));
      setError(null);
      setExportProgress({ current: 0, total: 0, currentName: '' });
    }
  }, [isOpen, currentReportId]);

  const toggleReport = (reportId) => {
    setSelectedReports((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(reportId)) {
        newSet.delete(reportId);
      } else {
        newSet.add(reportId);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    setSelectedReports(new Set(REPORTS.map(r => r.id)));
  };

  const selectNone = () => {
    setSelectedReports(new Set());
  };

  const handleExport = async () => {
    if (selectedReports.size === 0) {
      setError('Selecciona al menos un reporte');
      return;
    }

    setIsExporting(true);
    setError(null);

    const reportsToExport = REPORTS.filter(r => selectedReports.has(r.id));

    try {
      await onExportReports(reportsToExport, (current, total, currentName) => {
        setExportProgress({ current, total, currentName });
      });
      onClose();
    } catch (err) {
      console.error('Export error:', err);
      setError('Error al exportar. Intenta de nuevo.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={isExporting ? () => {} : onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-md w-full bg-white rounded-2xl shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-zinc-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-azul-surelia/10 rounded-lg flex items-center justify-center">
                <DocumentArrowDownIcon className="w-5 h-5 text-azul-surelia" />
              </div>
              <div>
                <Dialog.Title className="text-lg font-semibold text-azul-surelia">
                  Exportar Reportes
                </Dialog.Title>
                <p className="text-sm text-zinc-500">Año {year} - Un solo documento PDF</p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={isExporting}
              className="p-2 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4">
            {/* Quick actions */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={selectAll}
                disabled={isExporting}
                className="text-xs text-azul-surelia hover:underline disabled:opacity-50"
              >
                Seleccionar todos
              </button>
              <span className="text-zinc-300">|</span>
              <button
                onClick={selectNone}
                disabled={isExporting}
                className="text-xs text-azul-surelia hover:underline disabled:opacity-50"
              >
                Ninguno
              </button>
            </div>

            {/* Report list */}
            <div className="space-y-2">
              {REPORTS.map((report, index) => {
                const isSelected = selectedReports.has(report.id);
                return (
                  <button
                    key={report.id}
                    onClick={() => toggleReport(report.id)}
                    disabled={isExporting}
                    className={`w-full text-left p-3 rounded-xl border-2 transition-all ${
                      isSelected
                        ? 'border-azul-surelia bg-azul-surelia/5'
                        : 'border-zinc-200 hover:border-zinc-300'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                        isSelected ? 'text-azul-surelia' : 'text-zinc-300'
                      }`}>
                        {isSelected ? (
                          <CheckCircleIcon className="w-5 h-5" />
                        ) : (
                          <div className="w-4 h-4 rounded-full border-2 border-current" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-1.5 py-0.5 rounded ${isSelected ? 'bg-azul-surelia/20 text-azul-surelia' : 'bg-zinc-100 text-zinc-500'}`}>
                            {index + 1}
                          </span>
                          <span className={`font-medium ${isSelected ? 'text-azul-surelia' : 'text-zinc-700'}`}>
                            {report.title}
                          </span>
                        </div>
                        <div className="text-xs text-zinc-500 mt-0.5 ml-6">
                          {report.description}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Error message */}
            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                {error}
              </div>
            )}

            {/* Progress indicator */}
            {isExporting && (
              <div className="mt-4 p-3 bg-azul-surelia/5 border border-azul-surelia/20 rounded-lg">
                <div className="flex items-center gap-3">
                  <svg
                    className="animate-spin h-5 w-5 text-azul-surelia"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-azul-surelia">
                      Procesando reporte {exportProgress.current} de {exportProgress.total}
                    </div>
                    <div className="text-xs text-azul-surelia/70">
                      {exportProgress.currentName}
                    </div>
                  </div>
                </div>
                {/* Progress bar */}
                <div className="mt-2 h-1.5 bg-azul-surelia/20 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-azul-surelia transition-all duration-300"
                    style={{ width: `${(exportProgress.current / exportProgress.total) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-4 border-t border-zinc-200">
            <div className="text-xs text-zinc-500">
              {selectedReports.size > 0 && (
                <span>{selectedReports.size} reporte{selectedReports.size > 1 ? 's' : ''} seleccionado{selectedReports.size > 1 ? 's' : ''}</span>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                disabled={isExporting}
                className="px-4 py-2 text-sm font-medium text-zinc-600 hover:text-zinc-800 hover:bg-zinc-100 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleExport}
                disabled={isExporting || selectedReports.size === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-azul-surelia hover:bg-azul-surelia/90 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isExporting ? (
                  'Generando PDF...'
                ) : (
                  <>
                    <DocumentArrowDownIcon className="w-4 h-4" />
                    Exportar PDF
                  </>
                )}
              </button>
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
